import type { Maybe } from '@adornis/base/utilTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';

@Entity()
export class ExtractedCompanyInfo extends AdornisEntity {
  static override _class = 'ExtractedCompanyInfo';

  @Field(() => String) debitor: Maybe<string>;
  @Field(() => String) partnerID: Maybe<string>;
  /**
   * true if partnerID was picked as a generic fallback because the sender's email
   * could not be matched to any contact person for this debitor. UI should flag this
   * for human review.
   */
  @Field(() => Boolean) partnerIDFallback: Maybe<boolean>;
  /** Full name of the resolved contact person, shown next to the partner ID in the UI. */
  @Field(() => String) partnerName: Maybe<string>;
  /** Name of the sender as read from the email signature (raw, may contain typos). Used as a
   * fuzzy-matching signal to resolve the partner; not necessarily an exact contact name. */
  @Field(() => String) contactPersonName: Maybe<string>;
  @Field(() => String) name: Maybe<string>;
  @Field(() => String) address: Maybe<string>;
  @Field(() => String) city: Maybe<string>;
  @Field(() => String) zipCode: Maybe<string>;
  @Field(() => String) country: Maybe<string>;
  @Field(() => String) taxId: Maybe<string>;
  @Field(() => String) email: Maybe<string>;
  @Field(() => String) phone: Maybe<string>;
  @Field(() => String) website: Maybe<string>;

  constructor(data: Partial<ExtractedCompanyInfo>) {
    super(data);
    Object.assign(this, data);
  }
}

/**
 * The two things a header correction can be about. Kept apart because they are two different
 * decisions — "das ist die falsche Firma" and "das ist der falsche Ansprechpartner" — and a
 * change list that mixed them would make the second invisible next to the first.
 *
 * Order is display order. `partnerIDFallback` is deliberately absent: it is the extraction's
 * own bookkeeping, not a value a person chose.
 */
export const COMPANY_CORRECTION_FIELDS = [
  'debitor',
  'name',
  'address',
  'zipCode',
  'city',
  'country',
  'taxId',
  'email',
  'phone',
  'website',
] as const;

export const CONTACT_CORRECTION_FIELDS = ['partnerID', 'partnerName', 'contactPersonName'] as const;

/** Human-readable labels for both groups. Keys not listed fall back to the raw key. */
export const COMPANY_FIELD_LABELS: Record<string, string> = {
  debitor: 'Debitor',
  name: 'Firmenname',
  address: 'Adresse',
  zipCode: 'PLZ',
  city: 'Ort',
  country: 'Land',
  taxId: 'USt-IdNr.',
  email: 'E-Mail',
  phone: 'Telefon',
  website: 'Website',
  partnerID: 'Partner-ID',
  partnerName: 'Ansprechpartner',
  contactPersonName: 'Name aus Signatur',
};
